<template>
    <div :class="['EvieChatMessageAssistantBubble', { 'EvieChatMessageAssistantBubble--settled': isSettled }]">
        <EvieChatMessageError
            v-if="isError"
            :message="message"
            @retry="$emit('retry', message.feId)"
            @new-chat="$emit('new-chat')"
        />
        <template v-else>
            <EvieChatMessageMarkdown :text="message.content.text" />
            <div :class="['EvieChatMessageAssistantBubble__actions', { 'EvieChatMessageAssistantBubble__actions--visible': isLast && isSettled }]">
                <NewButton
                    variant="flat"
                    color="neutral"
                    size="icon-sm"
                    type="button"
                    aria-label="Copy message"
                    @click="copyText"
                >
                    <EidIcon :icon-src="copyIcon" />
                </NewButton>
                <template v-if="message.runId">
                    <NewButton
                        :class="['EvieChatMessageAssistantBubble__feedback', { 'EvieChatMessageAssistantBubble__feedback--active': message.feedbackScore === 'UP' }]"
                        variant="flat"
                        color="neutral"
                        size="icon-sm"
                        type="button"
                        aria-label="Thumbs up"
                        @click="$emit('feedback', { feId: message.feId, score: 'UP' })"
                    >
                        <EidIcon :icon-src="message.feedbackScore === 'UP' ? thumbsUpSolid : thumbsUpLight" />
                    </NewButton>
                    <NewButton
                        :class="['EvieChatMessageAssistantBubble__feedback', { 'EvieChatMessageAssistantBubble__feedback--active': message.feedbackScore === 'DOWN' }]"
                        variant="flat"
                        color="neutral"
                        size="icon-sm"
                        type="button"
                        aria-label="Thumbs down"
                        @click="$emit('feedback', { feId: message.feId, score: 'DOWN' })"
                    >
                        <span class="EvieChatMessageAssistantBubble__thumbsDownIcon">
                            <EidIcon :icon-src="message.feedbackScore === 'DOWN' ? thumbsUpSolid : thumbsUpLight" />
                        </span>
                    </NewButton>
                </template>
            </div>
        </template>
    </div>
</template>

<style lang="scss">
.EvieChatMessageAssistantBubble {
    max-width: 90%;

    &__actions {
        display: flex;
        gap: 2px;
        opacity: 0;
        transition: opacity 0.15s;

        &--visible,
        .EvieChatMessageAssistantBubble--settled:hover & {
            opacity: 1;
        }
    }

    &__feedback {
        &--active {
            color: var(--eidTrustGreen);
        }
    }

    &__thumbsDownIcon {
        display: inline-flex;
        transform: scale(-1, -1);
    }
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
    import { faCheck, faThumbsUp } from '@fortawesome/pro-light-svg-icons';
    import { faThumbsUp as faThumbsUpSolid } from '@fortawesome/pro-solid-svg-icons';

    import { faCopy } from '@evidentid/dashboard-commons/assets/custom-icon-definitions';
    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';

    import NewButton from '@/components/new-button/NewButton.vue';
    import { EvieThreadMessage } from '@/modules/evie-ai/models/EvieThreadMessage.model';

    import EvieChatMessageError from './EvieChatMessageError.vue';
    import EvieChatMessageMarkdown from './EvieChatMessageMarkdown.vue';

    export default defineComponent({
        name: 'EvieChatMessageAssistantBubble',
        components: { EvieChatMessageError, EvieChatMessageMarkdown, NewButton, EidIcon },
        props: {
            message: { type: Object as PropType<EvieThreadMessage>, required: true },
            isLast: { type: Boolean, default: false },
            isSettled: { type: Boolean, default: false },
        },
        emits: [ 'feedback', 'new-chat', 'retry' ],
        data() {
            return {
                copyIcon: faCopy as IconDefinition,
                copiedTimeout: null as ReturnType<typeof setTimeout> | null,
                thumbsUpLight: faThumbsUp as IconDefinition,
                thumbsUpSolid: faThumbsUpSolid as IconDefinition,
            };
        },
        computed: {
            isError(): boolean {
                return Boolean(this.message.errorType);
            },
        },
        beforeUnmount() {
            if (this.copiedTimeout) {
                clearTimeout(this.copiedTimeout);
            }
        },
        methods: {
            copyText() {
                navigator.clipboard.writeText(this.message.content.text);
                this.copyIcon = faCheck as IconDefinition;
                if (this.copiedTimeout) {
                    clearTimeout(this.copiedTimeout);
                }
                this.copiedTimeout = setTimeout(() => {
                    this.copyIcon = faCopy as IconDefinition;
                }, 1000);
            },
        },
    });
</script>
